/**
 * Plugin ZIP Upload Component (Ant Design v5)
 * 
 * WordPress-style drag-and-drop ZIP upload for plugins
 * 
 * Features:
 * - Drag and drop zone
 * - File picker
 * - Upload progress
 * - Validation feedback
 * - Success/error states
 */

import React, { useState, useRef, useCallback } from 'react';
import { Button, Alert, Progress, Card, Typography, Divider, message } from 'antd';
import { UploadOutlined, FileZipOutlined, CheckCircleOutlined, CloseCircleOutlined, RightOutlined } from '@ant-design/icons';
import { apiService } from '../services/apiService';
import { navigateToTab } from '../utils/navigation';

const { Title, Text, Paragraph } = Typography;

interface PluginZipUploadProps {
  onUploadSuccess?: (draftId: string, pluginName: string) => void;
  onUploadError?: (error: string) => void;
}

type UploadState = 'idle' | 'uploading' | 'success' | 'error';

const PluginZipUpload: React.FC<PluginZipUploadProps> = ({
  onUploadSuccess,
  onUploadError
}) => {
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingMessage, setProcessingMessage] = useState<string>('Processing plugin files...');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [result, setResult] = useState<{
    success: boolean;
    draft_id?: string;
    plugin_id?: string;
    plugin_name?: string;
    message: string;
    errors?: string[];
    warnings?: string[];
  } | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  
  // ============================================================================
  // Drag and Drop Handlers
  // ============================================================================
  
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    dragCounterRef.current++;
    
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);
  
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    dragCounterRef.current--;
    
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);
  
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsDragging(false);
    dragCounterRef.current = 0;
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelected(files[0]);
    }
  }, []);
  
  // ============================================================================
  // File Selection
  // ============================================================================
  
  const handleFileSelected = async (file: File) => {
    // Validate file type
    if (!file.name.toLowerCase().endsWith('.zip')) {
      setResult({
        success: false,
        message: 'Please select a ZIP file',
        errors: ['File must have .zip extension']
      });
      setUploadState('error');
      onUploadError?.('Invalid file type');
      message.error('Please select a ZIP file');
      return;
    }
    
    // Validate file size (50MB max)
    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      setResult({
        success: false,
        message: 'File too large',
        errors: [`File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds maximum (50MB)`]
      });
      setUploadState('error');
      onUploadError?.('File too large');
      message.error('File size exceeds 50MB limit');
      return;
    }
    
    setSelectedFile(file);
    setUploadState('idle');
    setResult(null);
    
    // Auto-start upload immediately after file is selected
    setTimeout(() => {
      handleUploadWithFile(file);
    }, 100);
  };
  
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelected(files[0]);
    }
  };
  
  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };
  
  // ============================================================================
  // Upload
  // ============================================================================
  
  const handleUploadWithFile = async (file: File) => {
    if (!file) return;
    
    setUploadState('uploading');
    setUploadProgress(0);
    setResult(null);
    
    try {
      // Upload file (fast - queues the job)
      const uploadResult = await apiService.uploadPluginZip(
        file,
        (progress) => setUploadProgress(progress)
      );
      
      if (!uploadResult.success) {
        setResult({
          success: false,
          message: uploadResult.message,
          errors: ['Upload failed']
        });
        setUploadState('error');
        onUploadError?.(uploadResult.message);
        message.error('Upload failed');
        return;
      }
      
      // Upload complete - show success immediately (extraction happens in background)
      setUploadProgress(100);
      setProcessingMessage('✅ Upload complete! Processing in background...');
      
      // Show success immediately - don't wait for extraction
      setResult({
        success: true, 
        draft_id: uploadResult.draft_id,
        plugin_name: uploadResult.plugin_name || 'Your Plugin',
        message: 'Plugin uploaded successfully! Extraction is happening in the background.',
        warnings: uploadResult.warnings
      });
      setUploadState('success');
      
      // Show success message
      message.success({
        content: '✅ Plugin uploaded! Go to Drafts to view it.',
        duration: 8,
        key: 'plugin-extract-status'
      });
      
      // DON'T poll - it causes 401 redirects. User can check Drafts tab for status.
      // The extraction happens in background via worker.
      
    } catch (error: any) {
      setResult({
        success: false,
        message: error.message || 'Upload failed',
        errors: [error.message || 'Unknown error']
      });
      setUploadState('error');
      onUploadError?.(error.message || 'Upload failed');
      message.error('Upload failed');
    }
  };
  
  const handleUpload = async () => {
    if (!selectedFile) return;
    await handleUploadWithFile(selectedFile);
  };
  
  const pollExtractionStatus = async (draftId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const status = await apiService.getZipExtractionStatus(draftId);

        // Update progress text if available
        if (status.progress) {
          setProcessingMessage(status.progress);
        }

        // Check for completed statuses
        if (status.status === 'built' || status.status === 'BUILT') {
          clearInterval(pollInterval);

          try {
            // Load draft to get plugin info
            const draft = await apiService.getDraft(draftId);

            setResult({
              success: true,
              draft_id: draftId,
              plugin_id: draft.plugin_id,
              plugin_name: draft.name,
              message: `Plugin '${draft.name}' extracted successfully!`,
              warnings: []
            });
            setUploadState('success');
            setProcessingMessage('✅ Extraction complete!');
            // Don't call onUploadSuccess - it causes parent refresh
            // onUploadSuccess?.(draftId, draft.name);
            message.success({
              content: `✅ Plugin "${draft.name}" is ready! Go to Drafts to configure it.`,
              duration: 8,
              key: 'plugin-extract-status'
            });
          } catch (err) {
            // If draft fetch fails, still show success with draft ID
            console.error('Error loading draft:', err);
            setResult({
              success: true,
              draft_id: draftId,
              plugin_name: 'Plugin',
              message: 'Plugin extracted successfully!',
              warnings: []
            });
            setUploadState('success');
            setProcessingMessage('✅ Extraction complete!');
            // Don't call onUploadSuccess - it causes parent refresh
            message.success({
              content: '✅ Plugin is ready! Go to Drafts to configure it.',
              duration: 8,
              key: 'plugin-extract-status'
            });
          }

        } else if (status.status === 'failed' || status.status === 'FAILED') {
          clearInterval(pollInterval);
          setResult({
            success: false,
            message: 'Extraction failed',
            errors: [status.error || 'Unknown error during extraction']
          });
          setUploadState('error');
          onUploadError?.(status.error || 'Extraction failed');
          message.error('Extraction failed');
        } else {
          // Still processing - update progress message
          const progressMsg = status.progress || `Processing... (${status.status})`;
          setProcessingMessage(progressMsg);
          console.log(`Extraction status: ${status.status}, progress: ${progressMsg}`);
        }

      } catch (error) {
        console.error('Error polling extraction status:', error);
        // Don't fail on polling errors - user can still go to drafts
      }
    }, 2000); // Poll every 2 seconds

    // Stop polling after 5 minutes
    setTimeout(() => clearInterval(pollInterval), 300000);
  };
  
  const handleReset = () => {
    setUploadState('idle');
    setSelectedFile(null);
    setResult(null);
    setUploadProgress(0);
    setProcessingMessage('Processing plugin files...');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // ============================================================================
  // Render
  // ============================================================================
  
  return (
    <div>
      {/* Drop Zone */}
      <Card
        hoverable={uploadState !== 'uploading'}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={uploadState !== 'uploading' ? handleBrowseClick : undefined}
        style={{
          padding: 32,
          textAlign: 'center',
          border: `2px dashed ${isDragging ? '#1890ff' : '#d9d9d9'}`,
          backgroundColor: isDragging ? '#e6f7ff' : 'transparent',
          cursor: uploadState !== 'uploading' ? 'pointer' : 'default',
          transition: 'all 0.3s ease'
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".zip"
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
          disabled={uploadState === 'uploading'}
        />
        
        {/* Icon */}
        <div style={{ marginBottom: 16, fontSize: '4rem' }}>
          {uploadState === 'uploading' ? '⏳' : 
           uploadState === 'success' ? '✅' :
           uploadState === 'error' ? '❌' :
           isDragging ? '📂' : '📦'}
        </div>
        
        {/* State-specific content */}
        {uploadState === 'idle' && !selectedFile && (
          <>
            <Title level={5}>Drag & Drop Plugin ZIP File</Title>
            <Text type="secondary">or click to browse files</Text>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Maximum file size: 50MB
              </Text>
            </div>
          </>
        )}
        
        {uploadState === 'idle' && selectedFile && (
          <>
            <Title level={5}>Ready to Upload</Title>
            <Text type="secondary" style={{ display: 'block' }}>
              {selectedFile.name}
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
            </Text>
          </>
        )}
        
        {uploadState === 'uploading' && (
          <>
            <Title level={5}>
              {uploadProgress < 100 ? 'Uploading...' : 'Extracting & Validating...'}
            </Title>
            <div style={{ maxWidth: 400, margin: '16px auto' }}>
              {uploadProgress < 100 ? (
                <>
                  <Progress percent={Math.floor(uploadProgress)} />
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                    {uploadProgress.toFixed(0)}% uploaded
                  </Text>
                </>
              ) : (
                <>
                  <Progress percent={100} status="active" />
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                    {processingMessage}
                  </Text>
                </>
              )}
            </div>
          </>
        )}
        
        {uploadState === 'success' && result && (
          <>
            <div style={{ marginBottom: 16 }}>
              <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
            </div>
            <Title level={5} style={{ color: '#52c41a', marginBottom: 8 }}>
              Plugin Uploaded Successfully!
            </Title>
            <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 16 }}>
              {result.plugin_name || 'Your Plugin'}
            </Text>
            {result.draft_id && (
              <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 12 }}>
                Draft ID: {result.draft_id}
              </Text>
            )}
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              Your plugin has been uploaded and saved to drafts. You can now configure and deploy it from the Drafts tab.
            </Paragraph>
          </>
        )}
        
        {uploadState === 'error' && (
          <>
            <Title level={5} style={{ color: '#ff4d4f' }}>
              Upload Failed
            </Title>
            <Text type="secondary">
              {result?.message || 'An error occurred'}
            </Text>
          </>
        )}
      </Card>
      
      {/* Action Buttons */}
      {selectedFile && uploadState === 'idle' && (
        <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
          <Button
            type="primary"
            size="large"
            icon={<UploadOutlined />}
            onClick={handleUpload}
            block
          >
            Upload Plugin
          </Button>
          <Button onClick={handleReset}>
            Cancel
          </Button>
        </div>
      )}
      
      {uploadState === 'success' && (
        <div style={{ display: 'flex', gap: 16, marginTop: 24 }}>
          <Button
            type="primary"
            size="large"
            icon={<RightOutlined />}
            onClick={() => {
              navigateToTab('drafts');
              // Also show a notification
              message.success({
                content: `Plugin "${result?.plugin_name}" is ready to configure in Drafts`,
                duration: 5,
              });
            }}
            block
          >
            Go to Drafts to Configure
          </Button>
          <Button onClick={handleReset}>
            Upload Another
          </Button>
        </div>
      )}
      
      {uploadState === 'error' && (
        <div style={{ marginTop: 16 }}>
          <Button
            type="primary"
            size="large"
            onClick={handleReset}
            block
          >
            Try Again
          </Button>
        </div>
      )}
      
      {/* Results */}
      {result && (
        <div style={{ marginTop: 24 }}>
          {result.success && (
            <Alert
              message="Plugin uploaded and extracted successfully!"
              description={
                <div>
                  <Text strong style={{ display: 'block', marginBottom: 8 }}>
                    {result.plugin_name}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                    Plugin ID: {result.plugin_id} | Draft ID: {result.draft_id}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    Navigate to the <strong>Drafts</strong> tab to review, configure, and deploy your plugin.
                  </Text>
                </div>
              }
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
              action={
                <Button
                  size="small"
                  type="link"
                  onClick={() => navigateToTab('drafts')}
                >
                  Go to Drafts
                </Button>
              }
            />
          )}
          
          {!result.success && result.errors && result.errors.length > 0 && (
            <Alert
              message="Validation Errors:"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {result.errors.map((error, idx) => (
                    <li key={idx} style={{ marginBottom: 4 }}>
                      <Text style={{ fontSize: 12 }}>{error}</Text>
                    </li>
                  ))}
                </ul>
              }
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          
          {result.warnings && result.warnings.length > 0 && (
            <Alert
              message="Warnings:"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {result.warnings.map((warning, idx) => (
                    <li key={idx} style={{ marginBottom: 4 }}>
                      <Text style={{ fontSize: 12 }}>{warning}</Text>
                    </li>
                  ))}
                </ul>
              }
              type="warning"
              showIcon
            />
          )}
        </div>
      )}
    </div>
  );
};

export default PluginZipUpload;

